import { gql } from '@apollo/client';
import { put, retry, takeEvery } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import SagaStatus from '../../../../Data/SagaStatus';
import ValidationStatus from '../../../../Data/ValidationStatus';
import PHONE_CONSTRAINT_NAMES from '../../../../Data/phoneConstraintNames';
import { query } from '../../../../Services/Gateway/Apollo';
import {
  setComponentValidationStatus,
  setPhoneValidationConstraint,
  setValidationConstraintStatus,
} from '../../slices/components';
import { setComponentIdToSagaStatus } from '../../slices/dataMappings';
import { handleAnswerQuestionSideEffectsSaga } from '../../../../Hooks/actionCreators';
import { incrementMetric } from '../../../../Services/Datadog';
import {
  validateGermanPhone,
  validateGermanPhoneAdvanced,
  hasValidPrefix,
  stripNonDigits,
} from './germanPhoneValidation';

export const VALIDATE_PHONE_NUMBER = gql`
  query ValidatePhone($phone: PhoneInput!) {
    validatePhone(phone: $phone) {
      isValidNumber
    }
  }
`;

const validatePhoneQuery = async ({ variables }) => {
  try {
    const response = await query({
      query: VALIDATE_PHONE_NUMBER,
      variables,
    });
    return response.data.validatePhone.isValidNumber;
  } catch (error) {
    return false;
  }
};

/**
 * Worker - requested by below function and carries out any async work
 */
export function* worker(action) {
  const { componentId, number, country, iFrameId, constraints, formId } =
    action.payload;

  yield put(
    setComponentIdToSagaStatus({
      componentId,
      status: SagaStatus.PENDING,
    })
  );

  const requestArguments = {
    phone: {
      number,
      country,
      validationMethod: 'gbpingphone',
    },
  };

  const SECOND = 1000;
  const args = {
    variables: requestArguments,
  };

  // German RegEx validation (toggled via germanRegex constraint from Capture)
  // Run before the Google API call so invalid numbers are rejected immediately
  // without waiting for a network round-trip.
  const isGermanRegexEnabled =
    constraints && constraints.germanRegex !== undefined;
  let germanRegexFailed = false;

  if (country === 'DE' && isGermanRegexEnabled) {
    let numberForValidation = number;
    const isAdvancedPhoneInput =
      constraints.phoneInternational && !hasValidPrefix(number);

    // For Advanced Phone Inputs (phoneInternational), the international/trunk
    // prefix may not be stored with the number. Normalise by prepending +49
    // so the prefix check does not produce a false negative.
    if (isAdvancedPhoneInput) {
      numberForValidation = `+49${stripNonDigits(number)}`;
    }

    // For Advanced Phone Inputs, validate digit count on the ORIGINAL number
    // before prefix normalisation, otherwise short numbers (e.g. 9 digits)
    // would incorrectly pass after +49 is prepended (adding 2 digits).
    const germanValidation = isAdvancedPhoneInput
      ? validateGermanPhoneAdvanced(number, numberForValidation)
      : validateGermanPhone(numberForValidation);

    if (!germanValidation.isValid) {
      germanRegexFailed = true;

      incrementMetric({
        name: 'germanPhoneRegexRejection',
        tags: {
          failed_rule: germanValidation.failedRule,
          form_id: formId,
        },
      });
    }
  }

  yield put(
    setPhoneValidationConstraint({
      componentId,
      status: ValidationStatus.PENDING,
    })
  );

  let isPhoneNumberValid = false;

  if (!germanRegexFailed) {
    isPhoneNumberValid = yield retry(3, 10 * SECOND, validatePhoneQuery, args);
  }

  if (isGermanRegexEnabled && constraints?.germanRegex) {
    yield put(
      setValidationConstraintStatus({
        componentId,
        constraintName: 'germanRegex',
        status: isPhoneNumberValid
          ? ValidationStatus.VALID
          : ValidationStatus.INVALID,
      })
    );
  }

  if (constraints) {
    const constraintsArray = Object.keys(constraints);
    // eslint-disable-next-line no-restricted-syntax
    for (const constraintName of constraintsArray) {
      if (PHONE_CONSTRAINT_NAMES.includes(constraintName)) {
        if (isPhoneNumberValid) {
          yield put(
            setValidationConstraintStatus({
              componentId,
              constraintName,
              status: ValidationStatus.VALID,
            })
          );
          /**
           * validate the rest of the components before the handle expose event
           */
          yield put(
            setComponentValidationStatus({
              componentId,
              countryCode: country,
              iFrameId,
              formId,
            })
          );
          yield put(handleAnswerQuestionSideEffectsSaga({ componentId }));
        } else {
          yield put(
            setValidationConstraintStatus({
              componentId,
              constraintName,
              status: ValidationStatus.INVALID,
            })
          );
          yield put(
            setComponentValidationStatus({
              componentId,
              countryCode: country,
              iFrameId,
              formId,
            })
          );
        }
      }
    }
  }

  yield put(
    setComponentIdToSagaStatus({
      componentId,
      status: SagaStatus.COMPLETE,
    })
  );
}

export const validatePhoneSaga = ({
  country,
  number,
  componentId,
  iFrameId,
  constraints,
  formId,
}) => ({
  type: ActionTypes.VALIDATE_PHONE_NUMBER,
  payload: {
    country,
    number,
    componentId,
    iFrameId,
    constraints,
    formId,
  },
});

/**
 * Listens for every VALIDATE_PHONE action that has been dispatched and will handle the saga of validating a phone number
 */
export default function* validatePhone() {
  yield takeEvery(ActionTypes.VALIDATE_PHONE_NUMBER, worker);
}
